import { Component, inject, Input, OnInit } from '@angular/core';
import { NonNullableFormBuilder, ReactiveFormsModule } from '@angular/forms';
import { IonLabel, IonSegment, IonSegmentButton, IonText, ModalController } from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { createSelector, Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';

import { PeopleToolbarComponent } from '@ninety/mobile/pages-shared/people-shared';
import {
  NtyFilterComponent,
  NtyToolbarFilterBtnComponent,
  NtyToolbarSortBtnComponent,
} from '@ninety/mobile/platform/components';
import { UserListSelectors } from '@ninety/mobile/platform/state';
import { TerraChipModule } from '@ninety/terra';

import { DiscussionFilterFields, DiscussionToolbarComponentActions } from '../../state/actions';
import { InitialListState } from '../../state/reducers/list.reducers';
import { selectFilterChips, selectListState, selectTotalDiscussionCount } from '../../state/selectors';
import { DiscussionParticipantSelectComponent } from '../forms/discussion-participant-select/discussion-participant-select.component';

@Component({
  selector: 'nty-discussion-toolbar',
  imports: [
    ReactiveFormsModule,
    IonLabel,
    IonSegment,
    IonSegmentButton,
    IonText,
    LetDirective,
    PeopleToolbarComponent,
    NtyFilterComponent,
    NtyToolbarFilterBtnComponent,
    NtyToolbarSortBtnComponent,
    DiscussionParticipantSelectComponent,
    TerraChipModule,
  ],
  templateUrl: './discussion-toolbar.component.html',
  styleUrls: ['./discussion-toolbar.component.scss'],
})
export class DiscussionToolbarComponent implements OnInit {
  @Input({ required: true }) currentUserId!: string;
  private readonly modal = inject(ModalController);

  private store = inject(Store);
  private fb = inject(NonNullableFormBuilder);

  private filterIsActiveBehaviorSubject = new BehaviorSubject(false);

  form = this.fb.group({
    selectedUserId: this.fb.control(InitialListState.filter.selectedUserId),
    showCompleted: this.fb.control(InitialListState.filter.showCompleted),
  });

  vm$ = {
    filterIsActive: this.filterIsActiveBehaviorSubject.asObservable(),
    filterableUsers: this.store.select(createSelector(UserListSelectors.selectActiveUsers, users => [...users])),
    sortDir: this.store.select(createSelector(selectListState, state => state.sort.sortDirection)),
    totalDiscussionCount: this.store.select(selectTotalDiscussionCount),
    filterChips: this.store.select(selectFilterChips),
  };

  onSortDirectionChange() {
    this.store.dispatch(DiscussionToolbarComponentActions.sortDirectionChanged());
  }

  async onApplyFilters() {
    this.filterIsActiveBehaviorSubject.next(true);
    this.store.dispatch(DiscussionToolbarComponentActions.listFiltered({ filters: this.form.getRawValue() }));
    await this.modal.dismiss(null, '', 'nty-filter-modal-id');
  }

  onClearFilters() {
    this.filterIsActiveBehaviorSubject.next(false);
    this.form.reset();
    this.store.dispatch(
      DiscussionToolbarComponentActions.listFiltered({
        filters: {
          ...this.defaultFilterValues,
        },
      })
    );
    this.form.controls.selectedUserId.setValue(this.defaultFilterValues.selectedUserId);
  }

  ngOnInit(): void {
    this.form.controls.selectedUserId.setValue(this.currentUserId);
  }

  private filterKeys: DiscussionFilterFields[] = ['selectedUserId', 'showCompleted'];

  /**
   * The value each filter field should fall back to when its chip is removed. `selectedUserId`
   * falls back to the current user (the silent default applied on load, {@link ngOnInit}) rather
   * than `InitialListState.filter.selectedUserId` ('all')
   */
  private get defaultFilterValues(): typeof InitialListState.filter {
    return { ...InitialListState.filter, selectedUserId: this.currentUserId };
  }

  removeFilter(field: DiscussionFilterFields): void {
    const filters = { ...this.form.getRawValue(), [field]: this.defaultFilterValues[field] };
    // Keep the filter form's control in sync with the dispatched change — otherwise reopening the
    // modal and hitting Apply without touching anything would re-dispatch the just-removed value.
    this.form.patchValue({ [field]: this.defaultFilterValues[field] });
    this.store.dispatch(DiscussionToolbarComponentActions.listFiltered({ filters }));

    const hasChanges = this.filterKeys.some(
      key => filters[key] !== undefined && filters[key] !== this.defaultFilterValues[key]
    );

    if (!hasChanges) {
      this.filterIsActiveBehaviorSubject.next(false);
    }
  }
}
